import {inject, Injectable} from '@angular/core';
import {LocalStorageService} from '@xmedical/core';
import {AuthenticationService} from '../auth/authentication.service';
import {OrganisationStore} from '../stores/organisation.store';
import {DraftKeyContext, PatientDraft, PatientDraftStore, PatientFormValue} from '@xmedical/clinical-shared';

/**
 * Location-portal implementation of `PatientDraftStore` (the lib's
 * `XCS_PATIENT_DRAFT_STORE` token).
 *
 * Persists in-progress new-patient form data to localStorage so a tab
 * refresh or accidental navigation does not lose user input. Namespaced
 * by `${orgId}-${practiceId}-${userId}` so:
 *
 *  - Drafts cannot leak across organisations (a user with multi-org
 *    membership won't see Org A's draft while editing in Org B).
 *  - Drafts cannot leak across practices within an org.
 *  - Drafts cannot leak across users on a shared device.
 *
 * The lib's `DraftKeyContext` only carries `practiceId`; the orgId is
 * read here from `OrganisationStore` so the lib contract stays unchanged.
 *
 * Edit-mode forms are not persisted — caller (the form) skips save()
 * when a patient is bound.
 */
@Injectable({ providedIn: 'root' })
export class LocalStoragePatientDraftStore implements PatientDraftStore {

  private storage = inject(LocalStorageService);
  private auth = inject(AuthenticationService);
  private orgStore = inject(OrganisationStore);

  save(value: PatientFormValue, ctx: DraftKeyContext): void {
    this.storage.setItem(this.key(ctx), value);
  }

  load(ctx: DraftKeyContext): PatientDraft | null {
    const raw = this.storage.getItem(this.key(ctx));
    if (!raw || typeof raw !== 'object') {
      return null;
    }
    return this.normalise(raw);
  }

  clear(ctx: DraftKeyContext): void {
    this.storage.deleteItem(this.key(ctx));
  }

  private key(ctx: DraftKeyContext): string {
    const orgId = this.orgStore.selectedOrganisationId() ?? 'unknown';
    const practiceId = ctx.practiceId ?? 'unknown';
    const userId = this.auth.getUserId() ?? 'unknown';
    return `organisation-portal.patient-draft.${orgId}.${practiceId}.${userId}`;
  }

  private normalise(raw: any): PatientDraft {
    const scalarised = nullsToEmpty(raw) as Record<string, any>;

    const contactNumbers = filterEntries(scalarised.contactNumbers);
    const emailAddresses = filterEntries(scalarised.emailAddresses);
    const addressDetails = filterEntries(scalarised.addressDetails);

    const billing = scalarised.billingContact ?? {};
    const billingContactContactNumbers = filterEntries(billing.billingContactContactNumbers);
    const billingContactEmailAddresses = filterEntries(billing.billingContactEmailAddresses);
    const billingContactAddressDetails = filterEntries(billing.billingContactAddressDetails);

    return {
      ...scalarised,
      contactNumbers,
      emailAddresses,
      addressDetails,
      billingContact: {
        ...billing,
        billingContactContactNumbers,
        billingContactEmailAddresses,
        billingContactAddressDetails,
      },
    } as PatientDraft;
  }
}

function nullsToEmpty(value: unknown): unknown {
  if (value === null || value === undefined) {
    return '';
  }
  if (Array.isArray(value)) {
    return value.map(nullsToEmpty);
  }
  if (typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const k of Object.keys(value as object)) {
      result[k] = nullsToEmpty((value as Record<string, unknown>)[k]);
    }
    return result;
  }
  return value;
}

function filterEntries(arr: unknown): any[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr.filter(item =>
    item &&
    typeof item === 'object' &&
    (item as any).type !== '' &&
    (item as any).type !== null &&
    (item as any).type !== undefined &&
    (item as any).label !== '' &&
    (item as any).label !== null &&
    (item as any).label !== undefined,
  );
}
